import {getAPIAxios, getAxios} from "../helper/constants/uri";
import useDiscountState from "../zustand/discountState";
import usePaymentCheckoutState from "../zustand/payment/checkout/paymentCheckoutState";
import {usePaymentCheckoutVoucherState} from "../zustand/payment/checkout/paymentCheckoutVoucherState";
import useAuthState from "../zustand/authState";

declare var window: any;

class PaymentInfoService {

    voucher = null;
    voucherCode = null;
    voucherStatus = null;

    errorHandlers = [];

    newTeaserText = null;
    newTeaserMobileText = null;
    newTeaserLink = null;
    newTeaserTextHandler = [];
    newVoucherHandler = [];

    async init(discountDetail: any = null) {

        if (this.voucher) {
            for (const h of this.newVoucherHandler) {
                h(this.voucher.teaser);
            }
        }

        if (discountDetail) {
            await this.setVoucherCodeInternal(discountDetail.code, discountDetail);
            return true;
        } else {
            if (typeof window !== 'undefined' && window.discount) {
                return await this.setVoucherCode(window.discount);
            }
        }
    }

    addErrorHandler(handler) {
        this.errorHandlers.push(handler);
    }

    raiseErrorHandler(tag = null) {
        for (const e of this.errorHandlers) {
            e(tag);
        }

        if (this.errorHandlers.length === 0) {
            if (tag === 'not-found') {
                this.voucherStatus = "not-found";
            } else if (tag === 'max-redemption') {
                this.voucherStatus = "max-redemption";
            }
        }
    }

    clearVoucherCode() {
        this.voucher = null;
        this.voucherCode = null;
        this.voucherStatus = null;
    }

    async setVoucherCode(newVoucherCode) {
        if (!newVoucherCode) {
            return false;
        }

        if (newVoucherCode === this.voucherCode) {
            // same code - so nothing to update
            return true;
        }

        // fetch all details about voucherCode from strapi
        try {
            useDiscountState.getState().setLoading(true);

            const axios = getAPIAxios();
            const voucher = (await axios.get("/discount/" + newVoucherCode)).data;

            await this.setVoucherCodeInternal(newVoucherCode, voucher);

        } catch (err) {

            // reset any existing one
            this.voucher = null;

            if (err?.response?.data?.error) {
                this.raiseErrorHandler(err.response.data.error);
                this.voucherStatus = err.response.data.error;
            } else {
                this.voucherStatus = "not-found";
                this.raiseErrorHandler("not-found");
            }

            useDiscountState.getState().setDiscount(null);

            // because the internal voucherStatus has changed
            await usePaymentCheckoutState.getState().recalculate();

            if (newVoucherCode) {
                usePaymentCheckoutVoucherState.getState().setCurrentInput(newVoucherCode);
            }

            return false;

        } finally {
            useDiscountState.getState().setLoading(false);
        }


        if (newVoucherCode) {
            usePaymentCheckoutVoucherState.getState().setCurrentInput(newVoucherCode);
        }

        return true;
    }

    async setVoucherSSR(voucher) {
        this.voucher = voucher;
    }

    async setVoucherCodeInternal(voucherCode, voucher) {
        this.voucherCode = voucherCode;
        this.voucher = voucher;

        if (this.voucher?.allowedProductsTag?.indexOf("basic") !== -1) {
            if (this.voucher?.allowedProductsTag?.indexOf("basic") !== -1) {
                const currentUserOverview = useAuthState.getState().currentUserOverview;

                if (currentUserOverview?.canRenewAbo && this.voucher.allowedProductsTag) {
                    this.voucher.allowedProductsTag = [  ...this.voucher.allowedProductsTag, "duo", "family" ];
                }
            }
        }

        useDiscountState.getState().setDiscount(this.voucher);

        if (this.voucher) {
            for (const h of this.newVoucherHandler) {
                h(this.voucher);
            }

            await usePaymentCheckoutState.getState().recalculate();
            usePaymentCheckoutVoucherState.getState().setCurrentInput(voucherCode);
        }

        // if there is a new teaser text - inform all handlers about it
        if (this.voucher.teaser) {

            // save in intermediate variable
            this.newTeaserText = this.voucher.teaser;
            this.newTeaserMobileText = this.voucher.teaserMobile;
            this.newTeaserLink = this.voucher.teaserBarLink;

            for (const h of this.newTeaserTextHandler) {
                h(this.voucher.teaser, this.voucher.teaserMobile, this.voucher.teaserBarLink,
                    this.voucher.countdownUnix, this.voucher.showCountdown, this.voucher.showSeconds,
                    this.voucher.showMode);
            }
        }
    }

    // only required for "einloesen" page - because voucher is already
    // loaded by page itself (for displaying possibilities)
    async setVoucherCodeAndVoucherDetail(newVoucherCode, voucherDetail) {
        await this.setVoucherCodeInternal(newVoucherCode, voucherDetail);

        localStorage?.setItem("DISCOUNT", JSON.stringify(
            {
                time: Date.now() + (48 * 60 * 60 * 1000),
                discount: newVoucherCode
            }
        ));

        window.discount = newVoucherCode;
    }

    calculatePrice(element, type, customAboPrice = undefined, giftcard = false) {
        // reset
        this.voucherStatus = null;

        if (type === 'course') {
            return this.calculatePriceForCourse(element);
        } else if (type === 'product') {
            return this.calculatePriceForProduct(element, customAboPrice, giftcard);
        }

        return null;
    }

    calculatePriceForCourse(course, discountPopup = true) {

        if (!course) {
            return null;
        }

        const startPrice = course.price;
        let discountPrice = 0;
        let discountPercentage = 0;
        let totalPrice = startPrice;
        let voucherTag = undefined;


        const courseId = course.id;

        if (this.canVoucherApplied(courseId, 'course', discountPopup, course)) {

            if (this.voucher.typus === 'fixed') {
                discountPrice = this.voucher.amount;
                totalPrice -= discountPrice;
            } else if (this.voucher.typus === 'percentage') {
                discountPrice = (course.price / 100.0 * this.voucher.amount);
                totalPrice -= discountPrice;
            }

            this.voucherStatus = "success";
            voucherTag = this.voucher.tag;

        } else if(this.voucher?.onlyProducts && !this.voucher?.onlyCourses){

            this.voucherStatus = "only-abo";

        } else if (this.voucher) {

            if (this.voucher?.code === "2min2mio" ||
                this.voucher?.code === "2m2m") {

            } else {
                // only set if really voucher is beneath it
                this.voucherStatus = "not-applicable";
            }

        }

        // if no input has happend and no voucher is available
        // then voucherStatus is meaningless
        if (!this.voucherCode && !this.voucher) {
            this.voucherStatus = null;
        }

        if (this.voucherCode === "blackweekendsale") {
            this.voucherStatus = "success";
        }

        // default return if no voucher is applied
        const info = {
            title: course.title,
            startPrice,
            discountPrice: Math.round((Math.max(discountPrice, 0) + Number.EPSILON) * 100) / 100.0,
            discountPercentage: discountPrice > 0 ? Math.round( (1 - (totalPrice / startPrice)) * 100 ) : 0,
            totalPrice: Math.round((Math.max(totalPrice, 0) + Number.EPSILON) * 100) / 100.0,
            subscription: false,
            voucherTag,
            voucherStatus: this.voucherStatus,
            voucherCode: this.voucher?.code
        };

        return info;
    }

    calculatePriceForProduct(product, customAboPrice = undefined, giftcard = false) {

        if (!product) {
            return null;
        }

        let startPrice = null;
        let discountPrice = 0;
        let totalPrice = product.price;
        let voucherTag = null;

        startPrice = product.price;

        let voucherDiscountPrice = 0;
        let voucherCanBeApplied = false;

        // if amount is exactly 0.0 for product - then no discount for product (but voucher is valid)
        if (this.canVoucherApplied(product.tag, 'product', false, undefined)) {
            voucherCanBeApplied = true;
            let voucherAmount = this.voucher.amountProduct ? this.voucher.amountProduct : this.voucher.amount;
            let typus = this.voucher.typus;
            if (this.voucher.typusProduct) {
                typus = this.voucher.typusProduct;
            }

            if (this.voucher.amountProduct === 0) {
                voucherDiscountPrice = 0.0;
                this.voucherStatus = "success";
            } else {

                if (typus === 'fixed') {
                    voucherDiscountPrice = voucherAmount;
                } else if (typus === "endprice") {
                    voucherDiscountPrice = Math.max( (product.price - voucherAmount), 0);
                } else {
                    voucherDiscountPrice = product.price / 100.0 * voucherAmount;
                }

                this.voucherStatus = "success";
            }

        }

        if (customAboPrice && !giftcard) {
            discountPrice = Math.abs(customAboPrice - startPrice);

            // prefer voucher over custom abo price
            if (voucherDiscountPrice > discountPrice) {
                totalPrice -= voucherDiscountPrice;
                voucherTag = this.voucher.tag;
            } else {
                totalPrice -= discountPrice;
                voucherTag = "customAboPrice";
            }

        } else if (voucherCanBeApplied) {
            totalPrice -= voucherDiscountPrice;
            discountPrice = voucherDiscountPrice;
            voucherTag = this.voucher.tag;
        } else {
            // check if maybe special case
            if (giftcard && this.voucher?.tag === "lifetimePrice") {
                this.voucherStatus = "only-abo";
            }
        }

        if (!this.voucher && (!this.voucherCode || (this.voucherCode.trim() === '')) ) {
            this.voucherStatus = null;
        }


        // round to two decimal
        const info = {
            title: product.title,
            startPrice: startPrice,
            totalPrice: Math.round( (totalPrice + Number.EPSILON) * 100 ) / 100.0,
            discountPrice,
            discountPercentage: discountPrice > 0 ? Math.round( (1 - (totalPrice / startPrice)) * 100 ) : 0,
            subscription: product.subscription,
            voucherStatus: this.voucherStatus,
            voucherTag,
            voucherCode: this.voucher?.code
        };

        return info;

    }

    canVoucherApplied(tagOrId, type, showPopup, element) {

        if (!this.voucher) {
            return false;
        }

        if (type === 'product') {
            if (this.voucher.onlyCourses) {
                return false;
            }


            if (this.voucher.allowedProductsTag) {
                const res = !!(this.voucher.allowedProductsTag?.find( productId => productId === tagOrId));
                return res;
            }

            return true;

        }

        if (type === 'course') {

            if ( !(this.voucher.amount === 100 || this.voucher.amount === 0 || this.voucher.amount === null)
                && element?.priceInstead > 0) {

                return false;
            }

            if (this.voucher.onlyProducts) {
                return false;
            }

            if (this.voucher.excludedCoursesId && this.voucher.excludedCoursesId.find(c => c == tagOrId)) {
                return false;
            }

            if (!this.voucher.allowedCoursesId) {
                return true;
            }  else {
                return !!(this.voucher.allowedCoursesId.find( courseId => courseId == tagOrId));
            }
        }

        return false;
    }

}

const defaultPaymentInfoServiceInstance = new PaymentInfoService();
export default defaultPaymentInfoServiceInstance;
